import type {
  DashboardFilters,
  DashboardKpiItem,
  DashboardProgressFlowItem,
  DashboardReportItem,
  DashboardSummary,
  DashboardWorkloadItem,
} from "@/types/dashboard";
import type { DashboardReportPreviewSnapshot } from "@/utils/dashboardReportPreview";

interface NormalizedKpiItem extends DashboardKpiItem {
  completion_rate: number;
  progress_percentage: number;
  open_tasks: number;
  total_tasks: number;
  completed_tasks: number;
  in_progress_tasks: number;
  latest_activity_at: string | null;
}

interface NormalizedReportItem extends DashboardReportItem {
  preview_url: string | null;
  pdf_url: string;
  created_at: string;
}

const EMPTY_REPORT_FILTERS: DashboardFilters = {
  client_id: "all",
  service_id: null,
  package_id: null,
  date_from: null,
  date_to: null,
};

const CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
}

function toStringValue(value: unknown, fallback = ""): string {
  if (value === null || value === undefined) return fallback;
  const stringified = String(value).trim();
  return stringified || fallback;
}

function readFirstValue(
  record: Record<string, unknown> | null | undefined,
  keys: string[]
): unknown {
  if (!record) return undefined;

  for (const key of keys) {
    if (key in record) {
      const value = record[key];
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
  }

  return undefined;
}

function escapeHtml(value: unknown): string {
  return toStringValue(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function formatDateInput(value: unknown): string {
  if (!value) return "";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function formatDate(value: unknown): string {
  if (!value) return "-";

  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return toStringValue(value, "-");
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatNumber(value: unknown): string {
  const numeric = toNumber(value, 0);
  return CURRENCY_FORMATTER.format(numeric);
}

function formatPercent(value: unknown): string {
  const numeric = Math.max(0, Math.min(100, toNumber(value, 0)));
  return `${numeric.toFixed(numeric % 1 === 0 ? 0 : 1)}%`;
}

function normalizeSummary(summary: unknown): DashboardSummary {
  const record = isRecord(summary) ? summary : {};
  const totalTasks = toNumber(
    readFirstValue(record, [
      "total_tasks",
      "totalTasks",
      "totalTickets",
      "total",
    ])
  );
  const inProgress = toNumber(
    readFirstValue(record, ["in_progress", "inProgress", "ongoingTickets"])
  );
  const completed = toNumber(
    readFirstValue(record, ["completed", "closedTickets", "done"])
  );
  const ticketRequests = toNumber(
    readFirstValue(record, ["ticket_requests", "ticketRequests"])
  );
  const newLeads = toNumber(readFirstValue(record, ["new_leads", "newLeads"]));
  const openTickets = toNumber(
    readFirstValue(record, ["open_tickets", "openTickets", "pending"])
  );
  const closedTickets = toNumber(
    readFirstValue(record, ["closed_tickets", "closedTickets", "resolved"]),
    completed
  );
  const averageProgress = toNumber(
    readFirstValue(record, [
      "average_progress",
      "completion_rate",
      "averageProgress",
    ])
  );

  return {
    totalTickets: totalTasks,
    notStartedTickets: toNumber(
      readFirstValue(record, ["not_started", "notStartedTickets"])
    ),
    ongoingTickets: inProgress,
    closedTickets,
    openTickets,
    ticketRequests,
    newLeads,
    total_tasks: totalTasks,
    in_progress: inProgress,
    completed,
    pending: openTickets,
    overdue: toNumber(readFirstValue(record, ["overdue"])),
    active_tickets: openTickets,
    ticket_requests: ticketRequests,
    new_leads: newLeads,
    completion_rate: averageProgress,
  };
}

function normalizeWorkloadByStatus(workload: unknown): DashboardWorkloadItem[] {
  if (!Array.isArray(workload)) return [];

  return workload
    .map((entry): DashboardWorkloadItem | null => {
      if (!isRecord(entry)) return null;

      const label = toStringValue(
        readFirstValue(entry, ["label", "status", "name", "title"]),
        "Unknown"
      );
      const value = toNumber(
        readFirstValue(entry, ["value", "count", "total"])
      );
      return {
        status: label,
        count: value,
      };
    })
    .filter((entry): entry is DashboardWorkloadItem => Boolean(entry));
}

function normalizeProgressFlow(
  progressFlow: unknown
): DashboardProgressFlowItem[] {
  if (!Array.isArray(progressFlow)) return [];

  return progressFlow
    .map((entry): DashboardProgressFlowItem | null => {
      if (!isRecord(entry)) return null;

      const date = toStringValue(
        readFirstValue(entry, ["date", "label", "day", "period"]),
        ""
      );
      const notStarted = toNumber(
        readFirstValue(entry, ["not_started", "notStarted", "queued"])
      );
      const inProgress = toNumber(
        readFirstValue(entry, ["in_progress", "inProgress", "active"])
      );
      const done = toNumber(readFirstValue(entry, ["done", "completed"]));
      const closed = toNumber(readFirstValue(entry, ["closed", "resolved"]));
      const overdue = toNumber(readFirstValue(entry, ["overdue"]));

      return {
        date: date || "-",
        not_started: notStarted,
        in_progress: inProgress,
        done,
        closed,
        overdue,
      };
    })
    .filter((entry): entry is DashboardProgressFlowItem => Boolean(entry));
}

function normalizeKpis(kpis: unknown): NormalizedKpiItem[] {
  if (!Array.isArray(kpis)) return [];

  return kpis
    .map((entry): NormalizedKpiItem | null => {
      if (!isRecord(entry)) return null;

      const completionRate = toNumber(
        readFirstValue(entry, [
          "completion_rate",
          "progress_percentage",
          "completionRate",
        ])
      );
      const progress = Math.max(0, Math.min(100, completionRate));
      const serviceId = readFirstValue(entry, ["service_id", "serviceId"]);
      const packageId = readFirstValue(entry, ["package_id", "packageId"]);

      return {
        service_id:
          serviceId === undefined || serviceId === null
            ? null
            : Number(serviceId),
        service_name: toStringValue(
          readFirstValue(entry, ["service_name", "serviceName"]),
          "Service"
        ),
        package_id:
          packageId === undefined || packageId === null
            ? null
            : Number(packageId),
        package_name: toStringValue(
          readFirstValue(entry, ["package_name", "packageName"]),
          "Package"
        ),
        kpi_name: toStringValue(
          readFirstValue(entry, ["kpi_name", "kpiName"]),
          "KPI"
        ),
        target: toNumber(readFirstValue(entry, ["target"])),
        actual: toNumber(readFirstValue(entry, ["actual"])),
        progress_percentage: progress,
        status: toStringValue(readFirstValue(entry, ["status"]), "Unknown"),
        total_tasks: toNumber(
          readFirstValue(entry, ["total_tasks", "totalTasks"])
        ),
        completed_tasks: toNumber(
          readFirstValue(entry, ["completed_tasks", "completedTasks"])
        ),
        open_tasks: toNumber(
          readFirstValue(entry, ["open_tasks", "openTasks"])
        ),
        in_progress_tasks: toNumber(
          readFirstValue(entry, ["in_progress_tasks", "inProgressTasks"])
        ),
        completion_rate: progress,
        latest_activity_at: toStringValue(
          readFirstValue(entry, ["latest_activity_at", "latestActivityAt"]),
          ""
        )
          ? toStringValue(
              readFirstValue(entry, ["latest_activity_at", "latestActivityAt"])
            )
          : null,
        health_status: toStringValue(
          readFirstValue(entry, ["health_status", "healthStatus"]),
          ""
        )
          ? toStringValue(
              readFirstValue(entry, ["health_status", "healthStatus"])
            )
          : undefined,
        sla_status: toStringValue(
          readFirstValue(entry, ["sla_status", "slaStatus"]),
          ""
        )
          ? toStringValue(readFirstValue(entry, ["sla_status", "slaStatus"]))
          : null,
        sla_deadline: toStringValue(
          readFirstValue(entry, ["sla_deadline", "slaDeadline"]),
          ""
        )
          ? toStringValue(
              readFirstValue(entry, ["sla_deadline", "slaDeadline"])
            )
          : null,
      };
    })
    .filter((entry): entry is NormalizedKpiItem => Boolean(entry));
}

function normalizeReportItem(report: unknown): NormalizedReportItem | null {
  if (!isRecord(report)) return null;

  const pdfUrl = toStringValue(
    readFirstValue(report, ["pdf_url", "pdfUrl"]),
    ""
  );

  return {
    id: toNumber(readFirstValue(report, ["id"])),
    report_id: toStringValue(
      readFirstValue(report, ["report_id", "reportId"]),
      ""
    ),
    title: toStringValue(readFirstValue(report, ["title"]), "Dashboard Report"),
    client_name: toStringValue(
      readFirstValue(report, ["client_name", "clientName"]),
      "All accessible accounts"
    ),
    service_name: toStringValue(
      readFirstValue(report, ["service_name", "serviceName"]),
      ""
    )
      ? toStringValue(readFirstValue(report, ["service_name", "serviceName"]))
      : null,
    package_name: toStringValue(
      readFirstValue(report, ["package_name", "packageName"]),
      ""
    )
      ? toStringValue(readFirstValue(report, ["package_name", "packageName"]))
      : null,
    report_period: toStringValue(
      readFirstValue(report, ["report_period", "reportPeriod"]),
      "-"
    ),
    status: toStringValue(readFirstValue(report, ["status"]), "Draft"),
    preview_url: toStringValue(
      readFirstValue(report, ["preview_url", "previewUrl"]),
      ""
    )
      ? toStringValue(readFirstValue(report, ["preview_url", "previewUrl"]))
      : null,
    pdf_url: pdfUrl || "",
    filters: isRecord(report.filters)
      ? (report.filters as unknown as DashboardFilters)
      : EMPTY_REPORT_FILTERS,
    created_at:
      formatDateInput(readFirstValue(report, ["created_at", "createdAt"])) ||
      "",
  };
}

function getReportTitle(snapshot: DashboardReportPreviewSnapshot): string {
  const datePart =
    formatDateInput(snapshot.generatedAt) ||
    new Date().toISOString().slice(0, 10);
  const scopePart = toStringValue(snapshot.scopeLabel, "Dashboard");
  return `Dashboard Report - ${scopePart} - ${datePart}`;
}

function getGeneratedLabel(snapshot: DashboardReportPreviewSnapshot): string {
  return formatDate(snapshot.generatedAt);
}

function getSummaryValue(summary: DashboardSummary, keys: string[]): number {
  for (const key of keys) {
    const value = (summary as unknown as Record<string, unknown>)[key];
    if (value !== undefined && value !== null) {
      return toNumber(value);
    }
  }

  return 0;
}

function getCurrentProgressValue(item: DashboardProgressFlowItem): number {
  return item.done + item.closed;
}

function getHeroSummaryText(summary: DashboardSummary): string {
  return `This report summarizes ${formatNumber(
    getSummaryValue(summary, ["total_tasks", "totalTickets"])
  )} total tasks, ${formatNumber(
    getSummaryValue(summary, ["in_progress", "ongoingTickets"])
  )} active items, ${formatNumber(
    getSummaryValue(summary, ["completed", "closedTickets"])
  )} completed items, ${formatNumber(
    getSummaryValue(summary, ["ticket_requests", "ticketRequests"])
  )} ticket requests, and ${formatNumber(
    getSummaryValue(summary, ["new_leads", "newLeads"])
  )} new leads for the selected scope.`;
}

function getExecutiveSummary(
  summary: DashboardSummary,
  latestReport: NormalizedReportItem | null,
  kpis: NormalizedKpiItem[]
): string {
  const totalTasks = getSummaryValue(summary, ["total_tasks", "totalTickets"]);
  const active = getSummaryValue(summary, ["in_progress", "ongoingTickets"]);
  const completed = getSummaryValue(summary, ["completed", "closedTickets"]);
  const openTickets = getSummaryValue(summary, ["open_tickets", "openTickets"]);
  const closedTickets = getSummaryValue(summary, [
    "closed_tickets",
    "closedTickets",
  ]);
  const progressAverage = kpis.length
    ? Math.round(
        kpis.reduce((sum, item) => sum + (item.progress_percentage || 0), 0) /
          kpis.length
      )
    : getSummaryValue(summary, ["average_progress", "completion_rate"]);

  const reportText = latestReport
    ? `The latest report is ${latestReport.title} for ${latestReport.report_period}.`
    : "No recent report has been recorded for this scope yet.";

  return [
    `The selected scope includes ${formatNumber(
      totalTasks
    )} total tasks with ${formatNumber(
      active
    )} currently active and ${formatNumber(completed)} completed.`,
    `Open tickets stand at ${formatNumber(
      openTickets
    )} and closed tickets at ${formatNumber(closedTickets)}.`,
    `Average service progress is ${formatPercent(progressAverage)}.`,
    reportText,
  ].join(" ");
}

function classifyHealth(status: string): string {
  const value = status.trim().toLowerCase();
  if (!value) return "Unknown";
  if (
    value.includes("healthy") ||
    value.includes("good") ||
    value.includes("stable")
  )
    return "Healthy";
  if (
    value.includes("warning") ||
    value.includes("watch") ||
    value.includes("attention")
  )
    return "Attention";
  if (
    value.includes("risk") ||
    value.includes("critical") ||
    value.includes("issue")
  )
    return "At Risk";
  if (
    value.includes("blocked") ||
    value.includes("paused") ||
    value.includes("stalled")
  )
    return "Blocked";
  if (value.includes("unknown")) return "Unknown";
  return status;
}

function getStatusTone(status: string): string {
  const key = status.trim().toLowerCase();
  if (
    key.includes("healthy") ||
    key.includes("good") ||
    key.includes("complete") ||
    key.includes("complete")
  )
    return "tone-good";
  if (
    key.includes("warning") ||
    key.includes("watch") ||
    key.includes("attention")
  )
    return "tone-warning";
  if (
    key.includes("risk") ||
    key.includes("critical") ||
    key.includes("blocked") ||
    key.includes("issue")
  )
    return "tone-danger";
  return "tone-info";
}

function getSafeAccountName(snapshot: DashboardReportPreviewSnapshot): string {
  return (
    snapshot.accountName || snapshot.scopeLabel || "All accessible accounts"
  );
}

function getServiceCoverageValue(kpis: NormalizedKpiItem[]): string {
  const packageCount = new Set(
    kpis.map((item) => item.package_name).filter((value) => Boolean(value))
  ).size;
  const serviceCount = new Set(
    kpis.map((item) => item.service_name).filter((value) => Boolean(value))
  ).size;

  return `${formatNumber(serviceCount)} services across ${formatNumber(
    packageCount
  )} packages`;
}

function getCoreServicesValue(kpis: NormalizedKpiItem[]): string {
  const services = Array.from(
    new Set(kpis.map((item) => item.service_name).filter(Boolean))
  ).slice(0, 3);

  if (!services.length) return "No service data yet";
  return services.join(", ");
}

function getAverageProgressValue(
  summary: DashboardSummary,
  kpis: NormalizedKpiItem[]
): string {
  const average = kpis.length
    ? kpis.reduce((sum, item) => sum + item.progress_percentage, 0) /
      kpis.length
    : getSummaryValue(summary, ["average_progress", "completion_rate"]);
  return formatPercent(average);
}

function getOpenTicketsValue(summary: DashboardSummary): string {
  return formatNumber(
    getSummaryValue(summary, ["open_tickets", "openTickets", "active_tickets"])
  );
}

function getClosedTicketsValue(summary: DashboardSummary): string {
  return formatNumber(
    getSummaryValue(summary, ["closed_tickets", "closedTickets", "completed"])
  );
}

function getLatestReportValue(report: NormalizedReportItem | null): string {
  if (!report) return "No recent report";

  return `${report.title} - ${report.report_period}`;
}

function getHealthMix(kpis: NormalizedKpiItem[]) {
  const palette: Record<string, string> = {
    Healthy: "#10b981",
    Attention: "#1f7aff",
    "At Risk": "#ef4444",
    Blocked: "#f59e0b",
    Unknown: "#94a3b8",
  };

  const counts = new Map<string, number>();
  for (const item of kpis) {
    const label = classifyHealth(
      item.health_status || item.status || "Unknown"
    );
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }

  const values = Array.from(counts.entries()).map(([label, value]) => ({
    label,
    value,
    color: palette[label] || palette.Unknown,
  }));

  const total = values.reduce((sum, item) => sum + item.value, 0);
  const segments = values.reduce<
    { label: string; color: string; start: number; end: number }[]
  >((acc, item) => {
    const previous = acc.length ? acc[acc.length - 1].end : 0;
    const size = total ? (item.value / total) * 100 : 0;
    acc.push({
      label: item.label,
      color: item.color,
      start: previous,
      end: previous + size,
    });
    return acc;
  }, []);

  return { values, total, segments };
}

function buildDonutGradient(
  segments: { color: string; start: number; end: number }[]
): string {
  if (!segments.length) {
    return "conic-gradient(#e5eef9 0% 100%)";
  }

  return `conic-gradient(${segments
    .map(
      (segment) =>
        `${segment.color} ${segment.start.toFixed(2)}% ${segment.end.toFixed(
          2
        )}%`
    )
    .join(", ")})`;
}

function buildWorkloadChart(workload: DashboardWorkloadItem[]): string {
  if (!workload.length) {
    return `
      <div class="empty-state">
        <h3>No workload data yet</h3>
        <p>The selected scope has not produced workload status data for this report.</p>
      </div>
    `;
  }

  const maxValue = Math.max(...workload.map((item) => item.count), 1);

  return `
    <div class="bar-list">
      ${workload
        .slice(0, 8)
        .map((item) => {
          const width = Math.max(4, Math.round((item.count / maxValue) * 100));
          return `
            <div class="bar-row">
              <div class="bar-row__head">
                <span class="bar-row__label">${escapeHtml(item.status)}</span>
                <span class="bar-row__value">${formatNumber(item.count)}</span>
              </div>
              <div class="bar-track">
                <span class="bar-fill" style="width:${width}%"></span>
              </div>
            </div>
          `;
        })
        .join("")}
    </div>
  `;
}

function buildProgressFlowChart(
  progressFlow: DashboardProgressFlowItem[]
): string {
  if (!progressFlow.length) {
    return `
      <div class="empty-state">
        <h3>No progress data yet</h3>
        <p>The current scope does not have enough history to draw the progress flow.</p>
      </div>
    `;
  }

  const maxValue = Math.max(
    ...progressFlow.map(
      (item) => getCurrentProgressValue(item) + item.in_progress + item.overdue
    ),
    1
  );

  return `
    <div class="flow-list">
      ${progressFlow
        .slice(0, 10)
        .map((item) => {
          const completed = getCurrentProgressValue(item);
          const active = item.in_progress;
          const overdue = item.overdue;
          const total = Math.max(completed + active + overdue, 1);
          const completedWidth = Math.max(
            2,
            Math.round((completed / maxValue) * 100)
          );
          const activeWidth = Math.max(
            2,
            Math.round((active / maxValue) * 100)
          );
          const overdueWidth = Math.max(
            2,
            Math.round((overdue / maxValue) * 100)
          );
          return `
            <div class="flow-row">
              <div class="flow-row__head">
                <span class="flow-row__label">${escapeHtml(
                  formatDate(item.date)
                )}</span>
                <span class="flow-row__value">${formatNumber(
                  total
                )} items</span>
              </div>
              <div class="flow-track">
                <span class="flow-fill flow-fill--completed" style="width:${completedWidth}%"></span>
                <span class="flow-fill flow-fill--active" style="width:${activeWidth}%"></span>
                <span class="flow-fill flow-fill--overdue" style="width:${overdueWidth}%"></span>
              </div>
              <div class="flow-meta">
                <span>Completed ${formatNumber(completed)}</span>
                <span>Active ${formatNumber(active)}</span>
                <span>Overdue ${formatNumber(overdue)}</span>
              </div>
            </div>
          `;
        })
        .join("")}
    </div>
  `;
}

function buildServiceProgressList(kpis: NormalizedKpiItem[]): string {
  if (!kpis.length) {
    return `
      <div class="empty-state">
        <h3>No service KPI data yet</h3>
        <p>Service and package performance will appear once the selected scope has tracked activity.</p>
      </div>
    `;
  }

  const rows = [...kpis]
    .sort((a, b) => b.progress_percentage - a.progress_percentage)
    .slice(0, 8);

  return `
    <div class="service-progress-list columns-2">
      ${rows
        .map(
          (item) => `
          <div class="service-progress-item">
            <div class="service-progress-item__head">
              <div>
                <p class="service-progress-item__title">${escapeHtml(
                  item.service_name
                )}</p>
                <p class="service-progress-item__sub">${escapeHtml(
                  item.package_name
                )}</p>
              </div>
              <span class="pill ${getStatusTone(item.status)}">${escapeHtml(
            item.status || "Unknown"
          )}</span>
            </div>
            <div class="service-progress-track">
              <span style="width:${Math.max(
                0,
                Math.min(100, item.progress_percentage)
              )}%"></span>
            </div>
            <div class="service-progress-meta">
              <span>${formatPercent(item.progress_percentage)}</span>
              <span>${formatNumber(item.open_tasks)} open tasks</span>
            </div>
          </div>
        `
        )
        .join("")}
    </div>
  `;
}

function buildHealthMixSection(kpis: NormalizedKpiItem[]): string {
  const mix = getHealthMix(kpis);

  if (!mix.total) {
    return `
      <div class="empty-state">
        <h3>No service health data yet</h3>
        <p>The current scope does not contain service health records yet.</p>
      </div>
    `;
  }

  return `
    <div class="health-wrap">
      <div class="donut-shell">
        <div class="donut" style="background:${buildDonutGradient(
          mix.segments
        )}">
          <div class="donut__center">
            <span class="donut__value">${formatNumber(mix.total)}</span>
            <span class="donut__label">services</span>
          </div>
        </div>
      </div>
      <div class="legend-list">
        ${mix.values
          .map(
            (item) => `
              <div class="legend-item">
                <span class="legend-swatch" style="background:${
                  item.color
                }"></span>
                <div>
                  <p class="legend-label">${escapeHtml(item.label)}</p>
                  <p class="legend-value">${formatNumber(
                    item.value
                  )} services</p>
                </div>
              </div>
            `
          )
          .join("")}
      </div>
    </div>
  `;
}

function buildTopRowsTable(kpis: NormalizedKpiItem[]): string {
  if (!kpis.length) {
    return `
      <div class="empty-state">
        <h3>No service details yet</h3>
        <p>Top KPI rows will appear here once services and packages are available.</p>
      </div>
    `;
  }

  const rows = [...kpis]
    .sort((a, b) => b.progress_percentage - a.progress_percentage)
    .slice(0, 8);

  return `
    <div class="table-wrap">
      <table class="report-table">
        <thead>
          <tr>
            <th>Package</th>
            <th>Service</th>
            <th>Progress</th>
            <th>Tasks</th>
            <th>Status</th>
            <th>Latest Activity</th>
          </tr>
        </thead>
        <tbody>
          ${rows
            .map((item) => {
              const totalTasks =
                item.total_tasks ||
                item.completed_tasks + item.open_tasks + item.in_progress_tasks;
              return `
                <tr>
                  <td>
                    <strong>${escapeHtml(item.package_name)}</strong>
                    <span>${escapeHtml(item.kpi_name)}</span>
                  </td>
                  <td>${escapeHtml(item.service_name)}</td>
                  <td>
                    <div class="inline-progress"><span style="width:${Math.max(
                      0,
                      Math.min(100, item.progress_percentage)
                    )}%"></span></div>
                    <span class="table-subtext">${formatPercent(
                      item.progress_percentage
                    )}</span>
                  </td>
                  <td>
                    <strong>${formatNumber(totalTasks)}</strong>
                    <span>${formatNumber(item.open_tasks)} open, ${formatNumber(
                item.in_progress_tasks
              )} active</span>
                  </td>
                  <td>
                    <span class="pill ${getStatusTone(
                      item.status
                    )}">${escapeHtml(item.status || "Unknown")}</span>
                  </td>
                  <td>
                    <strong>${escapeHtml(
                      formatDate(item.latest_activity_at)
                    )}</strong>
                    <span>${escapeHtml(
                      item.sla_status ||
                        item.sla_deadline ||
                        "No SLA target set"
                    )}</span>
                  </td>
                </tr>
              `;
            })
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function buildSummarySection(
  summary: DashboardSummary,
  kpis: NormalizedKpiItem[],
  latestReport: NormalizedReportItem | null
): string {
  const serviceCoverage = getServiceCoverageValue(kpis);
  const coreServices = getCoreServicesValue(kpis);
  const overallProgress = getAverageProgressValue(summary, kpis);
  const openTickets = getOpenTicketsValue(summary);
  const closedTickets = getClosedTicketsValue(summary);
  const latestReportLabel = getLatestReportValue(latestReport);

  const items = [
    {
      label: "Service coverage",
      value: serviceCoverage,
      description:
        "Active services and package presence for the selected scope.",
    },
    {
      label: "Core services",
      value: coreServices,
      description:
        "The most relevant services contributing to this dashboard view.",
    },
    {
      label: "Overall progress",
      value: overallProgress,
      description: "Average completion across package and service KPIs.",
    },
    {
      label: "Open tickets",
      value: openTickets,
      description: "Outstanding operational work requiring follow-up.",
    },
    {
      label: "Closed tickets",
      value: closedTickets,
      description: "Closed or completed support work for the current scope.",
    },
    {
      label: "Latest report",
      value: latestReportLabel,
      description: "Most recent generated report available for this scope.",
    },
  ];

  return `
    <div class="summary-grid">
      ${items
        .map(
          (item) => `
            <article class="summary-card">
              <p class="summary-card__label">${escapeHtml(item.label)}</p>
              <p class="summary-card__value">${escapeHtml(item.value)}</p>
              <p class="summary-card__copy">${escapeHtml(item.description)}</p>
            </article>
          `
        )
        .join("")}
    </div>
  `;
}

function buildMetadataNote(snapshot: DashboardReportPreviewSnapshot): string {
  const notes = [
    snapshot.dateRangeLabel ? `Date range: ${snapshot.dateRangeLabel}` : "",
    snapshot.filtersLabel ? `Filters: ${snapshot.filtersLabel}` : "",
  ].filter(Boolean);

  return notes.length
    ? `<div class="metadata-note">${notes
        .map((item) => `<span>${escapeHtml(item)}</span>`)
        .join("")}</div>`
    : "";
}

function buildPreparingBody(): string {
  return `
    <div class="state-shell">
      <div class="state-card">
        <p class="state-kicker">Dashboard Report Preview</p>
        <h1>Preparing report...</h1>
        <p>This preview uses the current dashboard data in a report-optimized layout.</p>
        <div class="state-spinner" aria-hidden="true"></div>
      </div>
    </div>
  `;
}

function buildErrorBody(message: string): string {
  return `
    <div class="state-shell">
      <div class="state-card state-card--error">
        <p class="state-kicker">Dashboard Report Preview</p>
        <h1>Unable to generate report preview</h1>
        <p>${escapeHtml(message)}</p>
        <div class="state-actions">
          <button class="toolbar-button toolbar-button--secondary" type="button" onclick="window.close()">
            Close
          </button>
        </div>
      </div>
    </div>
  `;
}

function buildReportBody(snapshot: DashboardReportPreviewSnapshot): string {
  const summary = normalizeSummary(snapshot.summary);
  const workload = normalizeWorkloadByStatus(snapshot.workloadByStatus);
  const progressFlow = normalizeProgressFlow(snapshot.progressFlow);
  const kpis = normalizeKpis(snapshot.servicePackageKpis);
  const latestReport = normalizeReportItem(snapshot.latestReport);
  const title = getReportTitle(snapshot);
  const accountName = getSafeAccountName(snapshot);
  const summaryNote = getHeroSummaryText(summary);
  const executiveSummary = getExecutiveSummary(summary, latestReport, kpis);
  const topRowsCount = Math.min(8, kpis.length);
  const allRowsCount = kpis.length;
  const topRowNote =
    allRowsCount > topRowsCount
      ? `Top ${topRowsCount} of ${allRowsCount} rows`
      : `Showing ${allRowsCount} rows`;
  const generatedLabel = getGeneratedLabel(snapshot);

  const serviceCoverage = getServiceCoverageValue(kpis);
  const averageProgress = getAverageProgressValue(summary, kpis);

  return `
    <div class="page">
      <header class="preview-toolbar no-print">
        <div>
          <p class="toolbar-kicker">DASHBOARD REPORT PREVIEW</p>
          <h1>Preview before printing or saving as PDF</h1>
          <p>This preview uses the current dashboard data in a report-optimized layout.</p>
        </div>
        <div class="toolbar-actions">
          <button class="toolbar-button toolbar-button--primary" type="button" onclick="window.print()">
            Print / Save as PDF
          </button>
          <button class="toolbar-button" type="button" onclick="window.close()">
            Close
          </button>
        </div>
      </header>

      <main class="report-shell">
        <section class="report-hero report-card">
          <div class="hero-copy">
            <p class="report-kicker">${escapeHtml(
              snapshot.reportTitle || "Dashboard Report"
            )}</p>
            <h1>Dashboard Analytics</h1>
            <p class="report-subtitle">${escapeHtml(
              snapshot.headerSubtitle || "Dashboard Analytics"
            )}</p>
            <p class="report-note">${escapeHtml(summaryNote)}</p>
          </div>

          <div class="hero-meta report-card">
            <div class="meta-grid">
              <div>
                <span class="meta-label">Generated</span>
                <p class="meta-value">${escapeHtml(generatedLabel)}</p>
              </div>
              <div>
                <span class="meta-label">Generated by</span>
                <p class="meta-value">${escapeHtml(
                  snapshot.generatedBy || "System"
                )}</p>
              </div>
              <div>
                <span class="meta-label">Account</span>
                <p class="meta-value">${escapeHtml(accountName)}</p>
              </div>
              <div>
                <span class="meta-label">Scope</span>
                <p class="meta-value">${escapeHtml(
                  snapshot.scopeLabel || "All accessible accounts"
                )}</p>
              </div>
            </div>
            ${buildMetadataNote(snapshot)}
          </div>
        </section>

        <section class="report-section report-card">
          <div class="section-heading">
            <div>
              <p class="section-kicker">Executive Summary</p>
              <h2>Operational snapshot</h2>
            </div>
          </div>
          <p class="section-copy">${escapeHtml(executiveSummary)}</p>
        </section>

        <section class="report-section">
          <div class="section-heading">
            <div>
              <p class="section-kicker">Main KPI Cards</p>
              <h2>Core dashboard totals</h2>
            </div>
            <p class="section-note">Primary totals for the selected scope.</p>
          </div>
          <div class="kpi-grid">
            ${buildKpiCard(
              "Total Tasks",
              formatNumber(
                getSummaryValue(summary, ["total_tasks", "totalTickets"])
              ),
              "All tracked tasks in scope.",
              "kpi-blue"
            )}
            ${buildKpiCard(
              "In Progress",
              formatNumber(
                getSummaryValue(summary, ["in_progress", "ongoingTickets"])
              ),
              "Currently active work items.",
              "kpi-cyan"
            )}
            ${buildKpiCard(
              "Completed",
              formatNumber(
                getSummaryValue(summary, ["completed", "closedTickets"])
              ),
              "Finished tasks and completed work.",
              "kpi-green"
            )}
            ${buildKpiCard(
              "Ticket Requests",
              formatNumber(
                getSummaryValue(summary, ["ticket_requests", "ticketRequests"])
              ),
              "Support or ticket-related requests.",
              "kpi-amber"
            )}
            ${buildKpiCard(
              "New Leads",
              formatNumber(getSummaryValue(summary, ["new_leads", "newLeads"])),
              "Lead intake captured in the selected period.",
              "kpi-violet"
            )}
          </div>
        </section>

        <section class="report-section report-grid-2">
          <article class="report-card report-panel">
            <div class="section-heading">
              <div>
                <p class="section-kicker">Workload by Status</p>
                <h2>Work distribution</h2>
              </div>
            </div>
            <div class="section-copy">${escapeHtml(
              workload.length
                ? "This chart shows how current work is distributed across status groups."
                : "No workload data yet."
            )}</div>
            ${buildWorkloadChart(workload)}
          </article>

          <article class="report-card report-panel">
            <div class="section-heading">
              <div>
                <p class="section-kicker">Progress Flow</p>
                <h2>Completion trends</h2>
              </div>
            </div>
            <div class="section-copy">${escapeHtml(
              progressFlow.length
                ? "This chart shows how work is moving through the reporting period."
                : "No progress data yet."
            )}</div>
            ${buildProgressFlowChart(progressFlow)}
          </article>
        </section>

        <section class="report-section report-card">
          <div class="section-heading">
            <div>
              <p class="section-kicker">Service / Package KPI Overview</p>
              <h2>Package and service performance</h2>
            </div>
          </div>
          <div class="overview-strip">
            <div class="overview-card">
              <span class="overview-label">Active packages</span>
              <strong>${escapeHtml(
                String(
                  new Set(kpis.map((item) => item.package_name).filter(Boolean))
                    .size
                )
              )}</strong>
            </div>
            <div class="overview-card">
              <span class="overview-label">Active services</span>
              <strong>${escapeHtml(
                String(
                  new Set(kpis.map((item) => item.service_name).filter(Boolean))
                    .size
                )
              )}</strong>
            </div>
            <div class="overview-card">
              <span class="overview-label">Open tasks</span>
              <strong>${escapeHtml(
                formatNumber(
                  kpis.reduce((sum, item) => sum + item.open_tasks, 0)
                )
              )}</strong>
            </div>
            <div class="overview-card">
              <span class="overview-label">Average progress</span>
              <strong>${escapeHtml(averageProgress)}</strong>
            </div>
          </div>

          <div class="report-grid-2 report-grid-2--overview">
            <article class="report-card report-subpanel">
              <div class="section-heading">
                <div>
                  <p class="section-kicker">Service Health Mix</p>
                  <h2>Status distribution</h2>
                </div>
              </div>
              ${buildHealthMixSection(kpis)}
            </article>

            <article class="report-card report-subpanel">
              <div class="section-heading">
                <div>
                  <p class="section-kicker">Service Progress</p>
                  <h2>Completion comparison</h2>
                </div>
              </div>
              ${buildServiceProgressList(kpis)}
            </article>
          </div>
        </section>

        <section class="report-section report-card">
          <div class="section-heading">
            <div>
              <p class="section-kicker">Top KPI Rows</p>
              <h2>Service details</h2>
            </div>
            <p class="section-note">${escapeHtml(topRowNote)}</p>
          </div>
          <p class="section-copy">${escapeHtml(serviceCoverage)}</p>
          ${buildTopRowsTable(kpis)}
        </section>

        <section class="report-section">
          <div class="section-heading">
            <div>
              <p class="section-kicker">Summary</p>
              <h2>Operational snapshot</h2>
            </div>
          </div>
          <p class="section-copy">The summary provides a quick operational snapshot of service coverage, progress, unresolved workload, and the latest available report output.</p>
          ${buildSummarySection(summary, kpis, latestReport)}
        </section>
      </main>
    </div>
    <script>
      document.title = ${JSON.stringify(title)};
      window.addEventListener("load", function () {
        setTimeout(function () {
          if (window.location.hash === "#print") {
            window.print();
          }
        }, 0);
      });
    </script>
  `;
}

function buildKpiCard(
  title: string,
  value: string,
  description: string,
  tone: string
): string {
  return `
    <article class="kpi-card ${tone}">
      <p class="kpi-label">${escapeHtml(title)}</p>
      <p class="kpi-value">${escapeHtml(value)}</p>
      <p class="kpi-copy">${escapeHtml(description)}</p>
    </article>
  `;
}

function buildDocument(title: string, body: string): string {
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge" />
    <title>${escapeHtml(title)}</title>
    <style>
      :root {
        color-scheme: light;
        --bg: #f4f8ff;
        --page: #ffffff;
        --ink: #0f172a;
        --muted: #64748b;
        --line: #dbe5f2;
        --soft: #eef4fb;
        --blue: #1f7aff;
        --blue-2: #0ea5e9;
        --green: #10b981;
        --amber: #f59e0b;
        --red: #ef4444;
        --violet: #8b5cf6;
        --shadow: 0 18px 40px rgba(15, 23, 42, 0.05);
      }

      * {
        box-sizing: border-box;
      }

      html,
      body {
        margin: 0;
        min-height: 100%;
        background: var(--bg);
        color: var(--ink);
        font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      body {
        overflow-x: hidden;
      }

      .page {
        min-height: 100vh;
      }

      .no-print {
        display: block;
      }

      .preview-toolbar {
        position: sticky;
        top: 0;
        z-index: 10;
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 20px;
        padding: 20px 24px;
        border-bottom: 1px solid var(--line);
        background: rgba(255, 255, 255, 0.94);
        backdrop-filter: blur(10px);
      }

      .toolbar-kicker,
      .section-kicker,
      .report-kicker,
      .state-kicker {
        margin: 0 0 6px;
        color: #2855a6;
        font-size: 0.72rem;
        font-weight: 900;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }

      .preview-toolbar h1,
      .state-card h1 {
        margin: 0;
        font-size: 1.15rem;
        font-weight: 900;
        color: var(--ink);
      }

      .preview-toolbar p,
      .state-card p {
        margin: 8px 0 0;
        color: var(--muted);
        line-height: 1.7;
      }

      .toolbar-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
      }

      .toolbar-button {
        border: 1px solid #d7e2ef;
        border-radius: 0.95rem;
        background: #ffffff;
        color: #173b5f;
        padding: 0.85rem 1rem;
        font-size: 0.88rem;
        font-weight: 800;
        cursor: pointer;
        box-shadow: 0 1px 0 rgba(255, 255, 255, 0.85) inset;
      }

      .toolbar-button--primary {
        border-color: #1f7aff;
        background: #1f7aff;
        color: #ffffff;
      }

      .toolbar-button--secondary {
        background: #f6f9ff;
        border-color: rgba(49, 125, 255, 0.22);
      }

      .report-shell {
        width: min(1180px, calc(100vw - 40px));
        margin: 24px auto 48px;
        display: grid;
        gap: 16px;
      }

      .report-card,
      .summary-card,
      .kpi-card,
      .overview-card,
      .service-progress-item,
      .flow-row,
      .bar-row,
      .state-card {
        break-inside: avoid;
        page-break-inside: avoid;
      }

      .report-hero {
        display: grid;
        grid-template-columns: minmax(0, 1.35fr) minmax(320px, 0.9fr);
        gap: 18px;
        align-items: stretch;
      }

      .hero-copy,
      .hero-meta,
      .report-section,
      .report-panel,
      .report-subpanel {
        background: var(--page);
      }

      .hero-copy {
        padding: 30px 28px;
        border-radius: 28px;
        border: 1px solid rgba(219, 229, 242, 0.85);
        box-shadow: var(--shadow);
        background:
          radial-gradient(circle at top right, rgba(151, 192, 255, 0.18), transparent 36%),
          linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(246, 249, 255, 0.98));
      }

      .hero-copy h1 {
        margin: 0;
        font-size: clamp(1.85rem, 2vw, 2.6rem);
        line-height: 1.05;
        font-weight: 900;
      }

      .report-subtitle {
        margin: 10px 0 0;
        color: #526177;
        font-size: 1rem;
        line-height: 1.7;
      }

      .report-note {
        margin: 14px 0 0;
        padding: 14px 16px;
        border-radius: 18px;
        background: #eef6ff;
        color: #173b5f;
        font-size: 0.95rem;
        line-height: 1.7;
        font-weight: 600;
      }

      .hero-meta {
        padding: 24px;
        border-radius: 28px;
        border: 1px solid rgba(219, 229, 242, 0.85);
        box-shadow: var(--shadow);
      }

      .meta-grid {
        display: grid;
        gap: 16px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .meta-label,
      .overview-label {
        display: block;
        font-size: 0.72rem;
        font-weight: 900;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: #748197;
      }

      .meta-value {
        margin: 8px 0 0;
        font-size: 0.96rem;
        font-weight: 800;
        color: var(--ink);
        line-height: 1.5;
      }

      .metadata-note {
        margin-top: 18px;
        display: grid;
        gap: 8px;
      }

      .metadata-note span {
        display: inline-flex;
        width: fit-content;
        padding: 0.45rem 0.7rem;
        border-radius: 999px;
        background: #f6f9ff;
        color: #173b5f;
        font-size: 0.78rem;
        font-weight: 700;
      }

      .report-section,
      .report-panel,
      .report-subpanel {
        padding: 24px;
        border-radius: 24px;
        border: 1px solid rgba(219, 229, 242, 0.85);
        box-shadow: var(--shadow);
      }

      .section-heading {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 12px;
      }

      .section-heading h2 {
        margin: 0;
        font-size: 1.18rem;
        font-weight: 900;
        color: var(--ink);
      }

      .section-note,
      .section-copy {
        margin: 0;
        color: var(--muted);
        font-size: 0.92rem;
        line-height: 1.7;
      }

      .kpi-grid {
        display: grid;
        gap: 14px;
        grid-template-columns: repeat(5, minmax(0, 1fr));
      }

      .kpi-card {
        min-height: 126px;
        padding: 18px;
        border-radius: 20px;
        border: 1px solid rgba(219, 229, 242, 0.9);
        box-shadow: var(--shadow);
      }

      .kpi-label {
        margin: 0;
        font-size: 0.74rem;
        font-weight: 900;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: #748197;
      }

      .kpi-value {
        margin: 10px 0 0;
        font-size: 1.9rem;
        line-height: 1.05;
        font-weight: 900;
        color: var(--ink);
      }

      .kpi-copy {
        margin: 12px 0 0;
        color: var(--muted);
        line-height: 1.6;
        font-size: 0.86rem;
      }

      .kpi-blue { background: linear-gradient(180deg, #ffffff, #f7faff); }
      .kpi-cyan { background: linear-gradient(180deg, #ffffff, #f4fbff); }
      .kpi-green { background: linear-gradient(180deg, #ffffff, #f5fdf8); }
      .kpi-amber { background: linear-gradient(180deg, #ffffff, #fffaf1); }
      .kpi-violet { background: linear-gradient(180deg, #ffffff, #faf7ff); }

      .report-grid-2 {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
      }

      .report-grid-2--overview {
        margin-top: 16px;
      }

      .overview-strip {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 12px;
        margin-top: 14px;
      }

      .overview-card {
        padding: 16px;
        border-radius: 18px;
        background: #f9fbff;
        border: 1px solid #e4ebf4;
      }

      .overview-card strong {
        display: block;
        margin-top: 8px;
        font-size: 1rem;
        line-height: 1.55;
        color: var(--ink);
      }

      .empty-state {
        padding: 18px;
        border-radius: 18px;
        border: 1px dashed #d8e4f0;
        background: #fbfcff;
      }

      .empty-state h3 {
        margin: 0;
        font-size: 0.98rem;
        font-weight: 900;
      }

      .empty-state p {
        margin: 8px 0 0;
        color: var(--muted);
        line-height: 1.7;
      }

      .bar-list,
      .flow-list,
      .legend-list {
        display: grid;
        gap: 12px;
      }

      .service-progress-list {
        display: block;
        columns: 2;
        gap: 12px;
      }

      .bar-row,
      .flow-row,
      .service-progress-item {
        padding: 14px 16px;
        border-radius: 18px;
        border: 1px solid #e4ebf4;
        background: #fbfcff;
      }

      .bar-row__head,
      .flow-row__head,
      .service-progress-item__head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
      }

      .bar-row__label,
      .flow-row__label,
      .service-progress-item__title {
        font-size: 0.86rem;
        font-weight: 900;
        color: var(--ink);
      }

      .bar-row__value,
      .flow-row__value,
      .service-progress-meta,
      .service-progress-item__sub,
      .table-subtext,
      .summary-card__copy {
        color: var(--muted);
        font-size: 0.82rem;
        line-height: 1.5;
      }

      .bar-track,
      .flow-track,
      .service-progress-track,
      .inline-progress {
        margin-top: 10px;
        height: 10px;
        overflow: hidden;
        border-radius: 999px;
        background: #e8eef7;
      }

      .bar-fill,
      .flow-fill,
      .service-progress-track span,
      .inline-progress span {
        display: block;
        height: 100%;
        border-radius: 999px;
      }

      .bar-fill {
        background: linear-gradient(90deg, var(--blue), var(--blue-2));
      }

      .flow-track {
        display: flex;
      }

      .flow-fill--completed {
        background: linear-gradient(90deg, #10b981, #34d399);
      }

      .flow-fill--active {
        background: linear-gradient(90deg, #1f7aff, #0ea5e9);
      }

      .flow-fill--overdue {
        background: linear-gradient(90deg, #ef4444, #fb7185);
      }

      .flow-meta,
      .service-progress-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 10px;
      }

      .service-progress-track span {
        background: linear-gradient(90deg, #1f7aff, #0ea5e9);
      }

      .pill {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.35rem 0.7rem;
        border-radius: 999px;
        border: 1px solid transparent;
        font-size: 0.72rem;
        font-weight: 900;
        white-space: nowrap;
      }

      .tone-good,
      .pill.healthy {
        background: #e6f8ec;
        color: #15803d;
        border-color: #d4f1df;
      }

      .tone-warning,
      .pill.attention {
        background: #fff3e3;
        color: #b45309;
        border-color: #ffe1af;
      }

      .tone-danger,
      .pill.risk,
      .pill.blocked {
        background: #fff1f2;
        color: #be123c;
        border-color: #fecdd3;
      }

      .tone-info,
      .pill.neutral {
        background: #e8f1ff;
        color: #2855a6;
        border-color: #d8e7ff;
      }

      .health-wrap {
        display: grid;
        grid-template-columns: 220px minmax(0, 1fr);
        gap: 18px;
        align-items: center;
        margin-top: 12px;
      }

      .donut-shell {
        display: grid;
        place-items: center;
      }

      .donut {
        width: 178px;
        height: 178px;
        border-radius: 50%;
        padding: 18px;
        box-shadow: inset 0 0 0 1px rgba(219, 229, 242, 0.9);
        display: grid;
        place-items: center;
      }

      .donut__center {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: #ffffff;
        display: grid;
        place-items: center;
        align-content: center;
        text-align: center;
        border: 1px solid #e4ebf4;
      }

      .donut__value {
        display: block;
        font-size: 1.7rem;
        line-height: 1;
        font-weight: 900;
      }

      .donut__label {
        display: block;
        margin-top: 6px;
        font-size: 0.8rem;
        font-weight: 800;
        color: var(--muted);
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }

      .legend-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 14px;
        border-radius: 16px;
        background: #fbfcff;
        border: 1px solid #e4ebf4;
      }

      .legend-swatch {
        width: 12px;
        height: 12px;
        border-radius: 999px;
        flex: none;
      }

      .legend-label {
        margin: 0;
        font-size: 0.84rem;
        font-weight: 900;
        color: var(--ink);
      }

      .legend-value {
        margin: 4px 0 0;
        font-size: 0.8rem;
        color: var(--muted);
      }

      .table-wrap {
        overflow-x: auto;
      }

      .report-table {
        width: 100%;
        min-width: 980px;
        border-collapse: collapse;
      }

      .report-table th,
      .report-table td {
        padding: 14px 12px;
        border-bottom: 1px solid #e9eff6;
        vertical-align: top;
        text-align: left;
      }

      .report-table thead th {
        background: #f8fbff;
        color: #748197;
        font-size: 0.72rem;
        font-weight: 900;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }

      .report-table td strong {
        display: block;
        color: var(--ink);
      }

      .report-table td span {
        display: block;
        margin-top: 4px;
        color: var(--muted);
        font-size: 0.8rem;
      }

      .inline-progress {
        margin-top: 0;
      }

      .summary-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 14px;
        margin-top: 14px;
      }

      .summary-card {
        padding: 18px;
        border-radius: 20px;
        background: #ffffff;
        border: 1px solid #e4ebf4;
        box-shadow: var(--shadow);
      }

      .summary-card__label {
        margin: 0;
        font-size: 0.74rem;
        font-weight: 900;
        color: #748197;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }

      .summary-card__value {
        margin: 10px 0 0;
        font-size: 1.02rem;
        line-height: 1.55;
        font-weight: 900;
        color: var(--ink);
      }

      .summary-card__copy {
        margin: 10px 0 0;
      }

      .state-shell {
        min-height: 100vh;
        display: grid;
        place-items: center;
        padding: 24px;
      }

      .state-card {
        width: min(560px, 100%);
        padding: 28px;
        border-radius: 28px;
        background:
          radial-gradient(circle at top right, rgba(151, 192, 255, 0.16), transparent 36%),
          #ffffff;
        border: 1px solid rgba(219, 229, 242, 0.9);
        box-shadow: var(--shadow);
      }

      .state-card--error {
        background:
          radial-gradient(circle at top right, rgba(248, 113, 113, 0.08), transparent 32%),
          #ffffff;
      }

      .state-actions {
        margin-top: 18px;
      }

      .state-spinner {
        width: 44px;
        height: 44px;
        margin-top: 18px;
        border-radius: 50%;
        border: 4px solid #dbe5f2;
        border-top-color: #1f7aff;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      @media (max-width: 1100px) {
        .report-shell {
          width: min(100vw - 24px, 1180px);
        }

        .report-hero,
        .report-grid-2,
        .summary-grid,
        .overview-strip {
          grid-template-columns: 1fr;
        }

        .kpi-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .health-wrap {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 720px) {
        .preview-toolbar {
          align-items: flex-start;
          flex-direction: column;
        }

        .kpi-grid {
          grid-template-columns: 1fr;
        }

        .meta-grid {
          grid-template-columns: 1fr;
        }
      }

      @media print {
        @page {
          size: A4 landscape;
          margin: 12mm;
        }

        body {
          background: #ffffff !important;
        }

        .no-print {
          display: none !important;
        }

        .page {
          background: #ffffff !important;
        }

        .report-shell {
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .report-card,
        .summary-card,
        .kpi-card,
        .overview-card,
        .service-progress-item,
        .flow-row,
        .bar-row,
        .report-table,
        .report-table tr,
        .report-table td,
        .report-table th {
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .report-grid-2,
        .summary-grid,
        .kpi-grid,
        .overview-strip {
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .report-table thead {
          display: table-header-group;
        }

        .report-table tfoot {
          display: table-footer-group;
        }
      }
    </style>
  </head>
  <body>
    ${body}
  </body>
</html>`;
}

export function buildDashboardReportHtml(
  snapshot: DashboardReportPreviewSnapshot
): string {
  const title = getReportTitle(snapshot);
  return buildDocument(title, buildReportBody(snapshot));
}

export function getPreparingReportHtml(): string {
  return buildDocument("Preparing Dashboard Report", buildPreparingBody());
}

export function getReportErrorHtml(error: unknown): string {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
      ? error
      : "Unable to generate the dashboard report preview right now.";

  return buildDocument("Dashboard Report Error", buildErrorBody(message));
}

export function writeDashboardReportPreviewWindow(
  reportWindow: Window,
  snapshot: DashboardReportPreviewSnapshot
): void {
  const html = buildDashboardReportHtml(snapshot);
  reportWindow.document.open();
  reportWindow.document.write(html);
  reportWindow.document.close();
  reportWindow.focus();
}

export function openDashboardReportPreviewWindow(
  snapshot: DashboardReportPreviewSnapshot
): Window | null {
  const reportWindow = window.open("", "_blank");

  if (!reportWindow) {
    return null;
  }

  writeDashboardReportPreviewWindow(reportWindow, snapshot);
  return reportWindow;
}

export { escapeHtml, formatDate, formatNumber, formatPercent, formatDateInput };
